import { Link } from 'react-router'
import { Badge } from '#app/components/ui/badge.tsx'
import { Card, CardContent } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'
import { DomainFavicon } from './domain-favicon'

interface BusinessCardProps {
	business: {
		id: string
		name: string
		slug: string
		isFeatured: boolean
		description: string | null
		aiDescription: string | null
		phone: string | null
		website: string | null
		address?: string | null
		categories?: Array<{
			id: string
			name: string
			slug: string
		}>
		subcategories?: Array<{
			id: string
			name: string
			slug: string
		}>
		cities?: Array<{
			id: string
			name: string
			slug: string
		}>
	}
	showAddress?: boolean
}

export function BusinessCard({ business }: BusinessCardProps) {
	return (
		<Link to={`/businesses/${business.slug}`} className="group">
			<Card className="dark:group-hover:ring-offset-background h-full transition-shadow group-hover:shadow-lg dark:group-hover:ring-4 dark:group-hover:ring-white/10">
				<CardContent className="flex flex-col justify-between overflow-hidden">
					<div className="space-y-4">
						<div className="flex items-center gap-2 leading-none font-medium">
							{business.website && (
								<DomainFavicon
									className="hidden rounded-lg md:block"
									domain={business.website}
									size={32}
								/>
							)}
							{business.name}
						</div>
						{business.description && (
							<p className="text-muted-foreground line-clamp-2 text-sm">
								{business.aiDescription || business.description}
							</p>
						)}

						{business.address && (
							<div className="text-muted-foreground mb-6 flex items-center gap-1 text-xs">
								<Icon name="map-pin" className="size-4" />
								{business.address}
							</div>
						)}
					</div>

					<div className="flex items-center justify-between">
						{business.cities && business.cities.length > 0 && (
							<div className="flex flex-wrap gap-1">
								{business.cities.map((city) => (
									<Badge key={city.id} variant="secondary">
										{city.name}
									</Badge>
								))}
							</div>
						)}
						<div className="flex flex-wrap gap-1">
							{business.categories && business.categories.length > 0 ? (
								business.categories.length === 1 ? (
									<Badge variant="secondary">
										{business.categories[0]?.name}
										{business.subcategories &&
											business.subcategories.length > 0 && (
												<span className="ml-1">
													+{business.subcategories.length}
												</span>
											)}
									</Badge>
								) : (
									<Badge variant="secondary">
										{business.categories[0]?.name} +
										{business.categories.length - 1}
										{business.subcategories &&
											business.subcategories.length > 0 && (
												<span className="ml-1">
													(+{business.subcategories.length} sub)
												</span>
											)}
									</Badge>
								)
							) : null}
						</div>
					</div>
				</CardContent>
			</Card>
		</Link>
	)
}
